// src/pages/Admin/NewsManager.jsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

/* ===================== styled ===================== */
const Page = styled.div`
  --gap: 16px;
  --radius: 12px;

  max-width: 1200px;
  margin: 0 auto;
  padding: calc(var(--header-height, 72px) + 24px) 16px 24px;
  padding-top: 10%;
  padding-bottom: 8rem;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gap);
`;

const Title = styled.h2`
  margin: 0;
  letter-spacing: -0.02em;
`;

const Right = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: var(--gap);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: var(--radius);
  background: #fff;
  overflow: hidden;
`;

const TableWrap = styled.div`
  max-height: 60vh;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Tr = styled.tr`
  border-top: 1px solid #f3f4f6;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#eef6ff' : 'transparent')};
  &:hover { background: ${({ $selected }) => ($selected ? '#e6f0ff' : '#fafafa')}; }
`;

const TrEmpty = styled.tr`
  td {
    padding: 16px;
    color: #6b7280;
    text-align: center;
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PanelTitle = styled.h3`
  margin: 16px 16px 8px;
`;

const Form = styled.div`
  display: grid;
  gap: 12px;
  padding: 0 16px 16px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const Label = styled.div`
  font-weight: 700;
  color: #374151;
`;

const Input = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .15);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ButtonBase = styled.button`
  height: 40px;
  padding: 0 14px;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform .05s ease, box-shadow .15s ease, background .15s ease, color .15s ease;
  &:active { transform: translateY(1px); }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const PrimaryButton = styled(ButtonBase)`
  background: #608cecff;
  color: #fff;
  &:hover { background: #1e4fd4; }
`;

const SecondaryButton = styled(ButtonBase)`
  color: #8e8d8dff;
  background: #fff;
  border: 1px solid #e5e7eb;
  &:hover { color: #131313; }
`;

const GhostButton = styled(ButtonBase)`
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  &:hover { background: #f9fafb; }
`;

const DangerButton = styled(ButtonBase)`
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  &:hover { background: #fecaca; }
`;

const ErrorAlert = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 10px 12px;
  border-radius: 10px;
  margin: 8px 0 12px;
  white-space: pre-wrap;
`;

/* ===================== component ===================== */
export default function NewsManager() {
    const API = process.env.REACT_APP_API_BASE || 'http://localhost:8080';
    const nav = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const [sel, setSel] = useState(null); // 선택된 뉴스
    const [form, setForm] = useState({ title: '', url: '', date: '' }); // yyyy.MM.dd
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr('');
        try {
            const r = await fetch(`${API}/api/news`, { credentials: 'include' });
            if (!r.ok) throw new Error(await r.text());
            const j = await r.json();
            setRows(Array.isArray(j) ? j : []);
        } catch (e) {
            setErr(e.message || '목록 조회 실패');
        } finally {
            setLoading(false);
        }
    };

    const checkAdmin = async () => {
        try {
            const r = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
            if (!r.ok) throw new Error('not admin');
        } catch {
            nav('/admin/login', { replace: true });
        }
    };

    useEffect(() => {
        checkAdmin();
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const validate = () => {
        if (!form.title.trim()) return '제목을 입력하세요.';
        if (!form.url.trim()) return '링크(URL)를 입력하세요.';
        if (!/^\d{4}\.\d{2}\.\d{2}$/.test(form.date)) return '작성 날짜 형식은 yyyy.MM.dd 입니다.';
        return null;
    };

    const resetForm = () => {
        setSel(null);
        setForm({ title: '', url: '', date: '' });
    };

    const createNews = async () => {
        if (saving) return;
        const v = validate();
        if (v) return alert(v);

        try {
            setSaving(true);
            const r = await fetch(`${API}/api/news`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            if (r.status === 401 || r.status === 403) return nav('/admin/login', { replace: true });
            if (!r.ok) throw new Error(await r.text());
            await load();
            resetForm();
            alert('등록되었습니다.');
        } catch (e) {
            alert('등록 실패: ' + (e.message || '오류'));
        } finally {
            setSaving(false);
        }
    };

    const updateNews = async () => {
        if (!sel || saving) return;
        const v = validate();
        if (v) return alert(v);

        try {
            setSaving(true);
            const r = await fetch(`${API}/api/news/${sel.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            if (r.status === 401 || r.status === 403) return nav('/admin/login', { replace: true });
            if (!r.ok) throw new Error(await r.text());
            await load();
            alert('수정되었습니다.');
        } catch (e) {
            alert('수정 실패: ' + (e.message || '오류'));
        } finally {
            setSaving(false);
        }
    };

    const deleteNews = async () => {
        if (!sel) return;
        if (!window.confirm('정말 삭제하시겠습니까?')) return;

        try {
            setSaving(true);
            const r = await fetch(`${API}/api/news/${sel.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (r.status === 401 || r.status === 403) return nav('/admin/login', { replace: true });
            if (r.status !== 204) throw new Error(await r.text());
            await load();
            resetForm();
            alert('삭제되었습니다.');
        } catch (e) {
            alert('삭제 실패: ' + (e.message || '오류'));
        } finally {
            setSaving(false);
        }
    };

    const pick = (item) => {
        setSel(item);
        setForm({ title: item.title || '', url: item.url || '', date: item.date || '' });
    };

    return (
        <Page>
            <TopBar>
                <Title>뉴스 관리</Title>
                <Right>
                    <GhostButton onClick={() => nav('/admin', { replace: true })}>대시보드</GhostButton>
                    <GhostButton onClick={resetForm}>새로 추가</GhostButton>
                    <PrimaryButton onClick={load} disabled={loading}>
                        {loading ? '불러오는 중…' : '목록 새로고침'}
                    </PrimaryButton>
                </Right>
            </TopBar>

            {err && <ErrorAlert>{err}</ErrorAlert>}

            <Grid>
                {/* 목록 */}
                <Card>
                    <TableWrap>
                        <Table>
                            <thead>
                            <tr>
                                <Th style={{ width: 90 }}>ID</Th>
                                <Th>제목</Th>
                                <Th style={{ width: 160 }}>작성일</Th>
                                <Th style={{ width: 80 }}>링크</Th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.map((r) => {
                                const selected = sel && sel.id === r.id;
                                return (
                                    <Tr key={r.id} $selected={selected} onClick={() => pick(r)}>
                                        <Td>{r.id}</Td>
                                        <Td title={r.title || ''}>{r.title || '(제목 없음)'}</Td>
                                        <Td>{r.date || '-'}</Td>
                                        <Td>
                                            {r.url && (
                                                <a
                                                    href={r.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    열기
                                                </a>
                                            )}
                                        </Td>
                                    </Tr>
                                );
                            })}

                            {rows.length === 0 && !loading && (
                                <TrEmpty>
                                    <td colSpan={4}>데이터 없음</td>
                                </TrEmpty>
                            )}
                            {loading && (
                                <TrEmpty>
                                    <td colSpan={4}>로딩 중…</td>
                                </TrEmpty>
                            )}
                            </tbody>
                        </Table>
                    </TableWrap>
                </Card>

                {/* 폼(신규/수정) */}
                <Card>
                    <PanelTitle>{sel ? `수정 #${sel.id}` : '신규 등록'}</PanelTitle>
                    <Form>
                        <Field>
                            <Label>제목</Label>
                            <Input name="title" value={form.title} onChange={onChange} placeholder="제목" />
                        </Field>

                        <Field>
                            <Label>링크(URL)</Label>
                            <Input name="url" value={form.url} onChange={onChange} placeholder="https://..." />
                        </Field>

                        <Field>
                            <Label>작성일 (yyyy.MM.dd)</Label>
                            <Input name="date" value={form.date} onChange={onChange} placeholder="2025.08.31" />
                        </Field>

                        <Actions>
                            {sel ? (
                                <>
                                    <PrimaryButton onClick={updateNews} disabled={saving}>
                                        {saving ? '저장 중…' : '수정 저장'}
                                    </PrimaryButton>
                                    <DangerButton onClick={deleteNews} disabled={saving}>
                                        삭제
                                    </DangerButton>
                                    <SecondaryButton onClick={resetForm} disabled={saving}>
                                        새로 추가
                                    </SecondaryButton>
                                </>
                            ) : (
                                <PrimaryButton onClick={createNews} disabled={saving}>
                                    {saving ? '등록 중…' : '등록'}
                                </PrimaryButton>
                            )}
                        </Actions>
                    </Form>
                </Card>
            </Grid>
        </Page>
    );
}